import { FileUploader, SignedURLUploadRequest, UploadRequest } from './file.uploader';
import { ApiService, UrlSanitizer } from '../api.service';
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { MTMCustomFile } from './mtm-custom-file';
import { IS_UNCANCELABLE_REQUEST } from '../../models';
import { TransferStatus } from './transfer-status';
import {
  listenerFileUploadCompleted,
  listenerTranscodingFileReplaced,
  listenerUploadProgressChanged,
  listenerUploadStatusChanged,
} from './listeners';
import { TransferProgress } from './transfer-progress';
import { EMPTY, Observable, Subject } from 'rxjs';
import { MalwareScanMonitorService } from './malware-scan-monitor.service';
import { MalwareScanningStatus } from '../../interfaces';
import { HttpHelperService } from '../http-helper.service';
import { FileUtilsService } from '../file-utils.service';

const ChunkSize = 16 * 1024 * 1024; //16Mb
const UploadProgressThrottle = 5000; //5s

@Injectable({
  providedIn: 'root',
})
export class FailedTranscodingUploader implements FileUploader {
  private readonly apiService = inject(ApiService);
  private readonly http = inject(HttpClient);
  private readonly scanMonitorService = inject(MalwareScanMonitorService);
  private readonly fileUtilsService = inject(FileUtilsService);
  private readonly httpHelperService = inject(HttpHelperService);
  private readonly supportedTypes = {
    pdf: 'application/pdf',
    png: 'image/png',
  };

  uploadFiles(request: UploadRequest) {
    this.constructPipeline(request);
    this.preUpload(request);
  }

  constructPipeline(request: UploadRequest) {
    request.files.forEach((item) => {
      item.pipelines = ['preUpload', 'upload', 'postUpload', 'scanning'];
      if (item.pipelines.length > 0) {
        item.step = item.pipelines[0];
        listenerUploadStatusChanged.next(item);
      }
      this.handleUploadStep(item);
    });
  }

  private handleUploadStep(mtmFile: MTMCustomFile) {
    if (!mtmFile.progress$) {
      mtmFile.progress$ = new Subject<TransferProgress>();
    }

    mtmFile.progress$.subscribe({
      next: (progress: TransferProgress) => {
        mtmFile.startIndex = progress.startIndex;

        switch (progress.step) {
          case 'next':
            this.proceedNextUploadStep(mtmFile);
            break;
          case 'error':
            if (!navigator.onLine) {
              mtmFile.uploadStatusCode = TransferStatus.ERROR;
              listenerUploadStatusChanged.next(mtmFile);
              return;
            } else {
              mtmFile.retryCount++;
              if (mtmFile.retryCount >= mtmFile.maxRetry) {
                mtmFile.uploadStatusCode = TransferStatus.ERROR;
                listenerUploadStatusChanged.next(mtmFile);
                return;
              }
            }
            break;
          case 'cancel':
            mtmFile.uploadStatusCode = TransferStatus.CANCELED;
            listenerUploadStatusChanged.next(mtmFile);
            return;
          default:
            if (progress.step) {
              mtmFile.step = progress.step;
            }
            break;
        }

        switch (mtmFile.step) {
          case 'postUpload':
            this.postUpload(mtmFile);
            break;
          case 'upload':
            if (mtmFile.startIndex == 0) {
              if (!mtmFile.sessionUrl) {
                this.initiateUpload(mtmFile);
              } else {
                this.uploadFileChunk(mtmFile);
              }
            } else {
              this.uploadFileChunk(mtmFile);
            }
            break;
          case 'scanning':
            this.waitForScanResult(mtmFile);
            break;
        }
      },
      error: (err) => {
        mtmFile.uploadStatusCode = TransferStatus.ERROR;
        listenerUploadStatusChanged.next(mtmFile);
      },
    });
  }

  private proceedNextUploadStep(mtmFile: MTMCustomFile) {
    let currentIndex = mtmFile.pipelines.indexOf(mtmFile.step);
    if (currentIndex > -1 && currentIndex < mtmFile.pipelines.length - 1) {
      currentIndex++;
      mtmFile.step = mtmFile.pipelines[currentIndex];
      listenerUploadStatusChanged.next(mtmFile);
      return;
    }

    mtmFile.step = 'done';
    listenerUploadStatusChanged.next(mtmFile);
    listenerFileUploadCompleted.emit(mtmFile);
    listenerTranscodingFileReplaced.emit(mtmFile);
    if (mtmFile.cleanUp) {
      mtmFile.cleanUp();
    } else {
      mtmFile.progress$.complete();
      mtmFile.progress$ = null;
    }
  }

  private preUpload(request: UploadRequest) {
    const mtmFile = request.files[0];
    const failedFileId = mtmFile.extraInfo.failedTranscodingFileId;
    const failedReplacementType = mtmFile.extraInfo.failedTranscodingReplacementType;
    //let preUploadUrl = `/api/commons/pre-upload-transcode-failed/${failedFileId}/${failedReplacementType}`;
    const preUploadUrl = `/api/commons/pre-upload-transcode-failed`;
    const preUploadParams = {
      fileId: failedFileId,
      extension: failedReplacementType,
      codec: failedReplacementType,
      contentType: this.supportedTypes[failedReplacementType],
      size: mtmFile.size,
      addedTime: Date.now(),
    };

    this.apiService.newPost(preUploadUrl, preUploadParams, { uncancelable: true }).subscribe({
      next: (result: any) => {
        mtmFile.setDbFileObject(result);
        mtmFile.preUploadUrl = preUploadUrl;
        mtmFile.extraInfo.failedTranscodingInitUrl = result.signedURL;
        mtmFile.progress$.next({
          status: TransferStatus.SENDING,
          startIndex: 0,
          step: 'next',
        });
      },
      error: (err) => {
        mtmFile.progress$.error({
          handled: true,
          error: err,
        });
      },
    });
  }

  private initiateUpload(file: MTMCustomFile) {
    const initialUrl = file.extraInfo.failedTranscodingInitUrl;
    const { progress$ } = file;
    const params: string[] = ['uploadType=resumable'];
    params.push(`name=${file.name}`);
    const initialUploadUrl: string = initialUrl;
    //don't encode the URL
    const safeurl = UrlSanitizer.sanitizeUrl(initialUploadUrl);

    file.currentRequest = this.http
      .post(
        safeurl,
        {},
        {
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
            'x-goog-resumable': 'start',
          },
          observe: 'response',
          context: new HttpContext().set(IS_UNCANCELABLE_REQUEST, true),
        }
      )
      .subscribe({
        next: (response: HttpResponse<any>) => {
          const sessionUrl: string = response.headers.get('Location') || '';
          if (!sessionUrl) {
            progress$.error('no location header for session url from metadata');
            return;
          }
          file.sessionUrl = sessionUrl;
          this.uploadFileChunk(file);
        },
        error: (error) => {
          console.error(error);
          progress$.error(error);
        },
      });
  }

  private uploadFileChunk(file: MTMCustomFile) {
    const { sessionUrl, startIndex } = file;
    const endIndex = Math.min(startIndex + ChunkSize, file.size) - 1;
    const uploadId = file.dbFileObject.id;
    let buffer = file.file.slice(startIndex, endIndex + 1);
    const locationResponseRe = /bytes=(\d+)\-(\d+)/;
    const safeUrl = UrlSanitizer.sanitizeUrl(sessionUrl);
    file.currentRequest = this.http
      .put(safeUrl, buffer, {
        headers: {
          'Content-Range': `bytes ${startIndex}-${endIndex}/${file.size}`,
        },
        observe: 'events',
        context: new HttpContext().set(IS_UNCANCELABLE_REQUEST, true),
        reportProgress: true,
      })
      .subscribe({
        next: (response: HttpEvent<ArrayBuffer>) => {
          switch (response.type) {
            case HttpEventType.UploadProgress:
              const curTimestamp = new Date().valueOf();
              if (curTimestamp - file.lastProgressTime < UploadProgressThrottle) {
                return;
              }

              let newPercentage = Math.floor(((file.startIndex + response.loaded) * 100) / file.fileSize);
              if (newPercentage > 100) {
                newPercentage = 100;
              }
              if (newPercentage == file.progress) {
                return;
              }
              file.progress = newPercentage;
              file.uploadStatusCode = TransferStatus.SENDING;
              listenerUploadProgressChanged.next(file);
              file.lastProgressTime = curTimestamp;
              break;

            case HttpEventType.Response:
              file.progress = 100;
              file.retryCount = 0;
              listenerUploadProgressChanged.next(file);
              file.progress$.next({
                status: TransferStatus.UPLOADED,
                startIndex: endIndex,
                step: 'next',
              });
              buffer = null;
              break;
          }
        },
        error: (error) => {
          if (error.status === 308) {
            const rangeResp = error.headers.get('Range') || '';
            const match = rangeResp.match(locationResponseRe);
            if (!match || match.length < 3) {
              file.progress$.error('failed to get range header');
              return;
            }
            file.retryCount = 0;
            const nextStart = parseInt(match[2].toString(), 10);
            file.progress$.next({
              status: TransferStatus.SENDING,
              startIndex: nextStart,
              step: '',
            });
            return;
          }
          file.progress$.next({
            status: TransferStatus.ERROR,
            startIndex,
            error: error.message,
            step: 'error',
          });
          buffer = null;
        },
      });
  }

  private performUpload(file: MTMCustomFile) {
    const signedUrl = file.sessionUrl;
    const headers: Record<string, string> = {};
    const httpHeaders = this.httpHelperService.getHttpHeaders();
    httpHeaders.keys().forEach((name) => {
      const values = httpHeaders.getAll(name);
      if (values && values.length && name !== 'Content-Type') {
        headers[name] = values[0];
      }
    });

    headers['Content-Type'] = file.file.type;
    //let safeUrl = UrlSanitizer.sanitizeUrl(signedUrl)
    file.currentRequest = this.http
      .put(signedUrl, file.file, {
        headers: headers,
        reportProgress: true,
        observe: 'events',
        context: new HttpContext().set(IS_UNCANCELABLE_REQUEST, true),
      })
      .subscribe({
        next: (response: any) => {
          switch (response.type) {
            case HttpEventType.UploadProgress:
              file.uploadStatusCode = TransferStatus.SENDING;
              const actualPercentage = (100 * response.loaded) / response.total;
              file.progress = Math.round(actualPercentage);
              if (file.progress > 0) {
                this.calculateRemainingTime(file, actualPercentage);
                listenerUploadProgressChanged.next(file);
              }
              break;
            case HttpEventType.Response:
              file.progress = 100;
              file.extraInfo.remainingTime = 0;
              file.uploadStatusCode = TransferStatus.UPLOADED;
              file.progress$.next({
                status: TransferStatus.UPLOADED,
                startIndex: file.fileSize,
                step: 'next',
              });
              break;
          }
        },
        error: (err) => {
          file.progress$.error({
            handled: true,
            error: err,
          });
        },
      });
  }

  private calculateRemainingTime(file: MTMCustomFile, percentage: number) {
    const elapsedTime = Date.now() - file.createTime;
    const expectedDuration = (100 / file.progress) * elapsedTime;
    file.extraInfo.remainingTime = expectedDuration - elapsedTime;
  }

  private postUpload(file: MTMCustomFile) {
    const failedFileId = file.extraInfo.failedTranscodingFileId;
    const url = `/api/commons/post-upload-transcode-failed`;
    const payload = {
      fileId: failedFileId,
      fileFallbackId: file.dbFileObject.id,
      codec: file.extraInfo.failedTranscodingReplacementType,
    };
    file.currentRequest = this.apiService.newPost(url, payload, { uncancelable: true }).subscribe({
      next: (dbElement: any) => {
        listenerUploadStatusChanged.emit(file);
        file.uploadStatusCode = TransferStatus.COMPLETED;
        file.progress$.next({
          status: TransferStatus.COMPLETED,
          startIndex: file.startIndex,
          step: 'next',
        });
      },
      error: (err) => {
        file.progress$.error({
          handled: true,
          error: err,
        });
      },
    });
  }

  cancelUpload(file: MTMCustomFile): Observable<any> {
    if (file.currentRequest) {
      file.currentRequest.unsubscribe();
    }
    return EMPTY;
  }

  private waitForScanResult(file: MTMCustomFile) {
    file.uploadStatusCode = TransferStatus.SCANNING;
    const update$ = this.scanMonitorService.monitorFile({
      fileId: file.dbFileObject.id,
      projectId: file.dbFileObject.projectId,
    });
    file.scanUpdateSubscription = update$.subscribe({
      next: (data: any) => {
        if (!data) {
          return;
        }
        this.fileUtilsService.copyFilePropsAfterScanning(data, file.dbFileObject);
        switch (data?.malwareScanningStatus) {
          case MalwareScanningStatus.Clean:
            file.uploadStatusCode = TransferStatus.COMPLETED;
            file.progress$.next({
              status: TransferStatus.COMPLETED,
              startIndex: file.fileSize,
              step: 'next',
            });
            file.scanUpdateSubscription?.unsubscribe();
            break;
          case MalwareScanningStatus.Quarantined:
            file.uploadStatusCode = TransferStatus.QUARANTINED;
            file.progress$.next({
              status: TransferStatus.QUARANTINED,
              startIndex: file.fileSize,
              step: 'next',
            });
            file.scanUpdateSubscription?.unsubscribe();
            break;
        }
      },
    });
  }
}
